import React, {
    type FC,
    type PropsWithChildren,
    useEffect,
    useMemo,
    useState,
} from "react";

import cn from "classnames";

import { SWITCH_TOGGLING_ANIMATION_DURATION } from "./vars";
import { extractProps, getChildrenRecord } from "../../utils/react";

import type { SwitchProps } from "./types";

import classes from "./Switch.styl";

const SWITCH_DISPLAY_NAME = "Switch";

/**
 * Компонент, который по сути представляет некоторое бинарное состояние, интерфейс чекбокса.
 * Помимо прочего, имеет состояние загрузки.
 *
 * <a href=" https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-16213&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 */
export const Switch: FC<PropsWithChildren<SwitchProps>> = ({
    children,
    ...props
}) => {
    const { customColorsStyles, currentProps } = useMemo(
        () => extractProps(props, ["customColorsStyles"]),
        [props],
    );
    const {
        view = "primary",
        state = "enabled",
        checked = false,
        onChange,
        name,
        "data-tsid": tsid,
        "aria-label": ariaLabel,
    } = currentProps;

    const isLoading = state === "loading";
    const isDisabled = state === "disabled";

    const [isPending, setIsPending] = useState<boolean>(false);

    const { Loader } = useMemo(
        () =>
            getChildrenRecord(children, "Switch", {
                Loader: {
                    props: {
                        customColorsStyles,
                        view: "custom",
                    },
                    required: isLoading,
                },
            }),
        [children, isLoading, customColorsStyles],
    );

    const isCheckedTemporary = isLoading !== checked;
    const isInactive = isDisabled || isLoading;
    const isLoadable = !!Loader;

    const switchWrapperClasses = cn(
        classes["switch-wrapper"],
        classes[`__view-${view}`],
        customColorsStyles,
        {
            [classes["__checked"]]: isCheckedTemporary,
            [classes["__inactive"]]: isInactive,
            [classes["__with-loader"]]: isLoadable,
        },
    );

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        if (state === "loading") {
            timer = setTimeout(
                () => setIsPending(true),
                SWITCH_TOGGLING_ANIMATION_DURATION,
            );
        } else {
            setIsPending(false);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [state]);

    return (
        <div className={switchWrapperClasses}>
            <input
                type="checkbox"
                name={name}
                checked={onChange ? checked : undefined}
                defaultChecked={!onChange ? checked : undefined}
                disabled={isInactive}
                className={classes["checkbox"]}
                data-tsid={tsid}
                data-uikit={SWITCH_DISPLAY_NAME}
                onChange={onChange}
                aria-label={ariaLabel}
            />
            <div className={classes["switch"]}>
                <div className={classes["rail"]}>
                    <div className={classes["knob"]}></div>
                </div>
            </div>
            {isLoadable && (
                <div
                    className={cn(classes["loader-overlay"], {
                        [classes["__shown"]]: isPending,
                    })}
                >
                    <div className={classes["rail"]}>
                        <div className={classes["knob"]}>{Loader}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

Switch.displayName = SWITCH_DISPLAY_NAME;

export default Switch;
